import { useCallback, useState } from 'react';
import { PureQueryOptions } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useMarkConvsHandledMutation,
  useRevertMsgTriageMutation,
} from 'core/graphql';

import { useGroupConfirm } from '../Groups/useGroupConfirm';
import { useToasts } from '../Toast/useToasts';
import { patchThreadTriage } from './threadCache';

/**
 * Bulk «Прочитано и обработано» (spec 0030) — the one entry point for every
 * scope of the action, sibling of `useMessageTriage` (which owns the
 * per-message tick). Both exist for the same reason: the cache consequences
 * of a triage flag moving are subtle enough that two copies WILL drift.
 *
 * ## Undo, not confirm (round 2)
 *
 * The action reports the exact ids it flipped and the toast offers
 * «Отменить». That is possible because both server-side UPDATEs only ever go
 * False → True, so clearing those very rows restores the prior state exactly
 * — re-deriving the set instead would also un-read messages that were already
 * read before the click. Past `undoable` the ids are too many to ship; the
 * confirm dialog stays the guard for those (group / everything-shown).
 *
 * ## Why refetch by operation NAME rather than evict
 *
 * Apollo is pinned at 3.3.6 here, so `client.refetchQueries({include})`
 * (3.4+) does not exist. What does exist — and is exactly right — is
 * `refetchQueries: ['OperationName']`, which refetches every mounted query
 * with that name REGARDLESS of its variables. That sidesteps having to guess
 * which `groupId`/filter combination is on screen, and it is immune to the
 * `keyArgs` string-matching that makes cache surgery brittle here.
 *
 * Watch the naming split: cache policies and `evict` use the FIELD name
 * (`conversationsBrowser`), `refetchQueries` uses the OPERATION name
 * (`DashboardConversations`). They are deliberately different in this repo.
 *
 * Eviction was rejected on purpose: evicting `groupsOverview` blanks the
 * left rail for a round-trip («нет групп» flashes), and `relayStylePagination`
 * already resets to page 1 on a refetch with no cursor — which is the state
 * you want right after a bulk clear.
 */

/** Scopes the server accepts. `filtered` means "everything the current
 *  browser filters select" — pass the SAME filters that produced the number
 *  shown in the confirm dialog, so the promise and the write agree. */
export type TBulkTriageScope =
  | 'conversation'
  | 'student'
  // ТЗ 0033: единственный способ погасить точку преподавателя оптом —
  // групповая кнопка его не трогает по решению владельца.
  | 'teacher'
  | 'group'
  | 'filtered';

export interface IBulkTriageVars {
  scope: TBulkTriageScope;
  conversationId?: string;
  studentId?: string;
  teacherId?: string;
  groupId?: string;
  kinds?: string[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  unhandledOnly?: boolean;
}

export interface IBulkTriageOptions {
  /** groupCommsIndex refetch descriptor from `useGroupCommsIndex`. Omit where
   *  no group badges are on screen — then there is nothing to re-pull. */
  badgeRefetch?: PureQueryOptions;
  /** Extra refetch after success — e.g. `refetchChannels` for
   *  `groupSlackChannels`, whose rows carry no id and cannot be patched. */
  onDone?: () => void;
}

/** Aggregates that no mutation payload can patch, refetched by OPERATION name
 *  so every mounted instance updates whatever its variables are. */
const AGGREGATE_QUERIES = [
  'DashboardConversations', // conversationsBrowser — queue membership changed
  'GroupMessagesFeed', // groupMessages(unreadOnly/unhandledOnly) membership
  'GroupSlackChannels', // GroupChannelRow: no id, unpatchable
  'GroupsOverviewList', // left-rail counters: no id, unpatchable
  // The detail cards' conversation lists. They ARE normalizable, so the
  // payload usually repaints them for free — but past CONV_PAYLOAD_CAP the
  // payload is truncated and only a refetch corrects the tail.
  //
  // ⚠️ ЭТО ИМЕНА ОПЕРАЦИЙ, А НЕ ПОЛЕЙ. Apollo сверяет строку с `queryName`
  // смонтированных запросов и на промах молчит. В ТЗ 0031 эти три операции
  // переименовали в `*Paged`, а строки здесь остались прежними — рефетч
  // полгода был тихим no-op. Держится тестом `useBulkTriage.queries.test.ts`,
  // который сверяет каждую строку с реальными документами.
  'StudentConversationsPaged',
  'GroupConversationsPaged',
  'TeacherConversationsPaged',
];

export const useBulkTriage = (
  options: IBulkTriageOptions = {},
): {
  /** Fire immediately — for cheap, obviously-scoped actions (one dialog, one
   *  student). No confirm: this feature exists to REMOVE clicks; the toast's
   *  «Отменить» is the safety net. */
  triage: (vars: IBulkTriageVars) => void;
  /** Ask first — for group / everything-shown, where the blast radius is not
   *  visible on screen. `body` is rendered in the dialog. */
  confirmAndTriage: (
    vars: IBulkTriageVars,
    confirm: { title: string; body: string; okLabel?: string },
  ) => void;
  loading: boolean;
} => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const askConfirm = useGroupConfirm();
  const { badgeRefetch, onDone } = options;
  const [mutate] = useMarkConvsHandledMutation();
  const [revert] = useRevertMsgTriageMutation();
  const [loading, setLoading] = useState(false);

  const refetchQueries = [
    ...(badgeRefetch ? [badgeRefetch] : []),
    ...AGGREGATE_QUERIES,
  ];

  const undo = useCallback(
    (readIds: string[], handledIds: string[], conversationId?: string) => {
      revert({
        variables: { read: readIds, handled: handledIds },
        refetchQueries,
        awaitRefetchQueries: false,
        update: (cache) => {
          if (conversationId) {
            patchThreadTriage(cache, conversationId, {
              read: false,
              handled: false,
            });
          }
        },
      })
        .then(({ data }) => {
          addToast({
            type: GlobalToastTypeNames.CommsBulkTriage,
            data: {
              type: data?.revertMessagesTriage?.success
                ? ToastTypes.info
                : ToastTypes.error,
              title: data?.revertMessagesTriage?.success
                ? t('pages.communications.bulkUndone')
                : t('pages.communications.bulkFailed'),
            },
          });
        })
        .catch(() => {
          addToast({
            type: GlobalToastTypeNames.CommsBulkTriage,
            data: {
              type: ToastTypes.error,
              title: t('pages.communications.bulkFailed'),
            },
          });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revert, addToast, t, badgeRefetch],
  );

  const triage = useCallback(
    (vars: IBulkTriageVars) => {
      setLoading(true);
      mutate({
        variables: vars,
        refetchQueries,
        // Never block the button's spinner on the refetches — GroupsOverviewList
        // is the slowest query in the app and the payload already repainted
        // everything the user is looking at.
        awaitRefetchQueries: false,
        update: (cache, { data }) => {
          const out = data?.markConversationsHandled;
          if (!out?.success) return;
          // Repaint the open thread for EVERY scope, not just 'conversation':
          // clicking a student's row while their thread is open used to clear
          // the row and leave the bubbles beside it looking unhandled — the
          // exact contradiction this helper exists to prevent.
          (out.conversations || []).forEach((conv) => {
            if (conv?.id) {
              patchThreadTriage(cache, String(conv.id), {
                read: true,
                handled: true,
              });
            }
          });
        },
      })
        .then(({ data }) => {
          const out = data?.markConversationsHandled;
          if (!out?.success) {
            addToast({
              type: GlobalToastTypeNames.CommsBulkTriage,
              data: {
                type: ToastTypes.error,
                title: t('pages.communications.bulkFailed'),
              },
            });
            return;
          }
          const messages = out.messagesAffected || 0;
          if (messages === 0) {
            // A repeat click is a genuine server-side no-op; reporting it as
            // «Готово: 0 сообщ.» reads like a failure.
            addToast({
              type: GlobalToastTypeNames.CommsBulkTriage,
              data: {
                type: ToastTypes.info,
                title: t('pages.communications.bulkNothingToDo'),
              },
            });
            onDone?.();
            return;
          }
          const readIds = (out.readMessageIds || []).map(String);
          const handledIds = (out.handledMessageIds || []).map(String);
          addToast({
            type: GlobalToastTypeNames.CommsBulkTriage,
            data: {
              type: ToastTypes.success,
              title: t('pages.communications.bulkDoneMessages', {
                count: messages,
              }),
              text: t('pages.communications.bulkDoneDialogs', {
                count: out.conversationsAffected || 0,
              }),
              ...(out.undoable
                ? {
                    actionLabel: t('pages.communications.undo'),
                    onAction: () =>
                      undo(readIds, handledIds, vars.conversationId),
                  }
                : {}),
            },
          });
          onDone?.();
        })
        .catch(() => {
          addToast({
            type: GlobalToastTypeNames.CommsBulkTriage,
            data: {
              type: ToastTypes.error,
              title: t('pages.communications.bulkFailed'),
            },
          });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutate, badgeRefetch, onDone, t, addToast, undo],
  );

  const confirmAndTriage = useCallback(
    (
      vars: IBulkTriageVars,
      confirm: { title: string; body: string; okLabel?: string },
    ) => {
      // The app's global confirm modal, not antd's and not window.confirm:
      // `erp-tms/CLAUDE.md` — "Do not render ad-hoc modals/toasts inside
      // pages". `useGroupConfirm` already wraps PopupNames.confirmPopup in a
      // promise, so there is nothing to re-invent here.
      askConfirm({
        title: confirm.title,
        message: confirm.body,
        confirmLabel: confirm.okLabel,
      }).then((ok) => {
        if (ok) triage(vars);
      });
    },
    [triage, askConfirm],
  );

  return { triage, confirmAndTriage, loading };
};
